import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Campaign } from "@/types/campaign";
import type { CaliNumber } from "@/types/number";

interface AssignNumbersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign | null;
  tenantId?: string | null;
  onSaved?: () => void;
}

export function AssignNumbersDialog({ open, onOpenChange, campaign, tenantId, onSaved }: AssignNumbersDialogProps) {
  const { toast } = useToast();
  const [numbers, setNumbers] = useState<CaliNumber[]>([]);
  const [existing, setExisting] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const filtered = useMemo(() => {
    if (!filter.trim()) return numbers;
    return numbers.filter(n => n.e164.toLowerCase().includes(filter.toLowerCase()));
  }, [numbers, filter]);

  useEffect(() => {
    async function load() {
      if (!open || !campaign || !tenantId) return;
      setLoading(true);
      try {
        const [{ data: nums, error: nErr }, { data: links, error: lErr }] = await Promise.all([
          supabase
            .from('calie_numbers')
            .select('number_id,e164,status,country,area_code')
            .order('e164', { ascending: true }),
          supabase
            .from('calie_campaign_numbers')
            .select('number_id')
            .eq('campaign_id', campaign.campaign_id),
        ]);
        if (nErr) throw nErr;
        if (lErr) throw lErr;
        setNumbers((nums || []) as CaliNumber[]);
        const ex = new Set<string>((links || []).map((l: any) => l.number_id));
        setExisting(ex);
        setSelected(new Set(ex));
      } catch (e: any) {
        toast({ title: "Failed to load numbers", description: e.message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [open, campaign, tenantId, toast]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const handleSave = async () => {
    if (!campaign) return;
    setSaving(true);
    try {
      const toInsert = Array.from(selected).filter(id => !existing.has(id)).map(id => ({
        campaign_id: campaign.campaign_id,
        number_id: id,
      }));
      if (toInsert.length > 0) {
        const { error } = await supabase.from('calie_campaign_numbers').insert(toInsert);
        if (error) throw error;
      }
      toast({ title: "Numbers assigned" });
      onSaved?.();
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: "Failed to assign numbers", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Assign numbers{campaign ? ` to ${campaign.name}` : ''}</DialogTitle>
          <DialogDescription>Select numbers to associate with this campaign.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="p-4 text-sm">Loading...</div>
        ) : (
          <div className="space-y-4">
            <Input placeholder="Search numbers" value={filter} onChange={(e) => setFilter(e.target.value)} />
            <ScrollArea className="h-64 border rounded-md p-2">
              <div className="space-y-2">
                {filtered.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No numbers found.</div>
                ) : filtered.map((n) => (
                  <label key={n.number_id} className="flex items-center gap-3 py-1 px-2 rounded hover:bg-accent cursor-pointer">
                    <Checkbox checked={selected.has(n.number_id)} onCheckedChange={() => toggle(n.number_id)} />
                    <div className="flex-1">
                      <div className="font-mono">{n.e164}</div>
                      <div className="text-xs text-muted-foreground">{n.country || '—'} {n.area_code ? `• ${n.area_code}` : ''}</div>
                    </div>
                  </label>
                ))}
              </div>
            </ScrollArea>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save assignments'}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
